import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ProviderBadge } from '@/components/ui/ProviderBadge';
import { Button } from '@/components/ui/button';
import { Loader2, Zap, Battery, CircuitBoard, Settings2, RefreshCw, ChevronDown, ChevronRight, Power } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { t } from '@/lib/vocabulary';
import { BatteryHealthSection } from '@/components/system/BatteryHealthSection';
import { SkeletonCard } from '@/components/dashboard/SkeletonLoader';
interface EnergySystem {
  id: string;
  display_name: string | null;
  device_sn: string | null;
  role: string;
  is_primary: boolean;
  is_active: boolean;
  provider_type: string;
  last_poll_at: string | null;
  last_poll_status: string | null;
  consecutive_failures: number;
  backoff_until: string | null;
  plant_uuid: string | null;
}

interface PlantWithSystems {
  plant: {
    id: string;
    display_name: string;
    provider_type: string | null;
    provider_plant_id: string | null;
  };
  systems: EnergySystem[];
}

interface GeneratorStatus {
  existsGen: boolean;
  genOn: boolean;
  genPower: number;
}

/**
 * Shell-first: full-page loader only while app-context loads; then shell (header, content area).
 * Content shows SkeletonCard until fetchSystems resolves.
 */
export default function System() {
  const { properties, plants, selectedPropertyId, selectedPlantId, loading: appLoading, isHomeowner } = useApp();
  const [systems, setSystems] = useState<EnergySystem[]>([]);
  const [generatorStatus, setGeneratorStatus] = useState<GeneratorStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedPlants, setExpandedPlants] = useState<Set<string>>(new Set());

  const selectedProperty = properties.find(p => p.id === selectedPropertyId);
  const propertyPlants = plants.filter(p => p.property_id === selectedPropertyId);

  const fetchSystems = async () => {
    if (!selectedPropertyId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('energy_systems')
        .select('*')
        .eq('property_id', selectedPropertyId)
        .order('is_primary', { ascending: false });

      if (error) throw error;
      setSystems(data ?? []);
      
      // Auto-expand all plants
      const plantIds = new Set(propertyPlants.map(p => p.id));
      setExpandedPlants(plantIds);

      // Fetch generator status from latest telemetry (plants-latest; fallback to RPC when function not deployed)
      if (selectedPlantId) {
        try {
          const { data: telemetryData, error: invokeError } = await supabase.functions.invoke('plants-latest', {
            body: { plantId: selectedPlantId },
          });
          if (telemetryData) {
            setGeneratorStatus({
              existsGen: telemetryData.existsGen ?? false,
              genOn: telemetryData.genOn ?? false,
              genPower: telemetryData.genPower_w ?? 0,
            });
          } else if (invokeError?.message?.includes('404') || (invokeError as { status?: number })?.status === 404) {
            const { data: rows } = await supabase.rpc('get_latest_plant_telemetry', {
              p_plant_id: selectedPlantId,
            });
            const row = Array.isArray(rows) && rows.length > 0 ? (rows[0] as { plant_gen_power_w?: number }) : null;
            if (row && row.plant_gen_power_w != null) {
              const genW = Number(row.plant_gen_power_w);
              setGeneratorStatus({
                existsGen: true,
                genOn: genW > 0,
                genPower: genW,
              });
            }
          }
        } catch {
          // Ignore errors - generator status is optional
        }
      }
    } catch (err) {
      console.error('Error fetching systems:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchSystems();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when selection changes; omit fetchSystems to avoid loop
  }, [selectedPropertyId, selectedPlantId]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchSystems();
  };

  const togglePlant = (plantId: string) => {
    setExpandedPlants(prev => {
      const next = new Set(prev);
      if (next.has(plantId)) {
        next.delete(plantId);
      } else {
        next.add(plantId);
      }
      return next;
    });
  };

  const getPollingStatus = (system: EnergySystem) => {
    if (system.backoff_until && new Date(system.backoff_until) > new Date()) {
      return { label: 'Backoff', className: 'bg-status-stale/20 text-status-stale border-status-stale/30' };
    }
    if (system.consecutive_failures >= 3) {
      return { label: 'Failing', className: 'bg-status-offline/20 text-status-offline border-status-offline/30' };
    }
    if (system.last_poll_status === 'success') {
      return { label: 'Healthy', className: 'bg-status-online/20 text-status-online border-status-online/30' };
    }
    if (system.last_poll_status === 'error') {
      return { label: 'Error', className: 'bg-status-stale/20 text-status-stale border-status-stale/30' };
    }
    return { label: 'Unknown', className: 'bg-muted text-muted-foreground' };
  };

  // Group systems by plant
  const plantsWithSystems: PlantWithSystems[] = propertyPlants.map(plant => ({
    plant: {
      id: plant.id,
      display_name: plant.display_name,
      provider_type: plant.provider_type,
      provider_plant_id: plant.provider_plant_id,
    },
    systems: systems.filter(s => s.plant_uuid === plant.id),
  }));

  // Orphan systems (not linked to any plant)
  const orphanSystems = systems.filter(s => !s.plant_uuid);

  // Full-page loader only until app-context is ready; then shell + SkeletonCard until systems resolve.
  if (appLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-wt-text-secondary" />
      </div>
    );
  }

  if (!selectedPropertyId || !selectedProperty) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">System</h1>
          <p className="text-wt-text-secondary mt-1">View plants, equipment, and batteries</p>
        </div>
        <Card className="bg-wt-bg-secondary border-border">
          <CardContent className="py-12 text-center">
            <CircuitBoard className="w-12 h-12 mx-auto text-wt-text-secondary mb-4" />
            <p className="text-wt-text-secondary">No property selected.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header — shell always visible once app-context ready */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">System</h1>
          <p className="text-wt-text-secondary mt-1">
            {loading
              ? 'Loading…'
              : `${selectedProperty.name} — ${propertyPlants.length} plant${propertyPlants.length !== 1 ? 's' : ''}, ${systems.length} device${systems.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw className={cn('w-4 h-4 mr-2', refreshing && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Content: SkeletonCard until fetchSystems resolves */}
      {loading ? (
        <div className="space-y-4">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : plantsWithSystems.length === 0 && orphanSystems.length === 0 ? (
        <Card className="bg-wt-bg-secondary border-border">
          <CardContent className="py-12 text-center">
            <CircuitBoard className="w-12 h-12 mx-auto text-wt-text-secondary mb-4" />
            <h3 className="text-lg font-medium mb-2">No equipment configured</h3>
            <p className="text-wt-text-secondary">
              Add plants and inverters through the onboarding wizard.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
        <div className="space-y-4">
          {plantsWithSystems.map(({ plant, systems: plantSystems }) => (
            <Card key={plant.id} className="bg-wt-bg-secondary border-border">
              <Collapsible open={expandedPlants.has(plant.id)} onOpenChange={() => togglePlant(plant.id)}>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-secondary/30 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        {expandedPlants.has(plant.id) ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                        <Zap className="w-5 h-5 text-wt-text-secondary" />
                        {plant.display_name}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        {plant.provider_type && (
                          <ProviderBadge provider={plant.provider_type} />
                        )}
                        <Badge variant="secondary" className="text-xs">
                          {plantSystems.length} device{plantSystems.length !== 1 ? 's' : ''}
                        </Badge>
                      </div>
                    </div>
                    {plant.provider_plant_id && (
                      <CardDescription className="ml-10">
                        Plant ID: {plant.provider_plant_id}
                      </CardDescription>
                    )}
                  </CardHeader>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <CardContent className="pt-0">
                    {plantSystems.length === 0 ? (
                      <p className="text-sm text-wt-text-secondary py-4 text-center">
                        No devices linked to this plant.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {plantSystems.map(system => {
                          const pollStatus = getPollingStatus(system);
                          return (
                            <div 
                              key={system.id}
                              className="flex items-center justify-between p-4 rounded-lg bg-secondary/30"
                            >
                              <div className="flex items-center gap-3">
                                <CircuitBoard className="w-5 h-5 text-wt-text-secondary" />
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">
                                      {system.display_name || system.device_sn || 'Unknown Device'}
                                    </span>
                                    {system.is_primary && (
                                      <Badge className="text-xs bg-muted text-muted-foreground border-border">
                                        Primary
                                      </Badge>
                                    )}
                                    {!system.is_active && (
                                      <Badge variant="outline" className="text-xs opacity-50">
                                        Inactive
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="text-sm text-wt-text-secondary flex items-center gap-2">
                                    <span className="font-mono text-xs">{system.device_sn}</span>
                                    <span>•</span>
                                    <span className="capitalize">{system.role}</span>
                                    {system.last_poll_at && (
                                      <>
                                        <span>•</span>
                                        <span>{t('lastPoll', isHomeowner)} {formatDistanceToNow(new Date(system.last_poll_at), { addSuffix: true })}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-3">
                                <Badge variant="outline" className={pollStatus.className}>
                                  {pollStatus.label}
                                </Badge>
                                <Button variant="ghost" size="sm" className="text-wt-text-secondary">
                                  <Settings2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          ))}
        </div>

      {/* Battery Configuration — only when data loaded */}
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Battery className="w-5 h-5 text-neon-purple" />
            Battery Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <span className="text-sm text-wt-text-secondary block">Total Capacity</span>
              <span className="text-2xl font-mono-numeric font-bold text-neon-purple">
                {selectedProperty.battery_kwh_total} <span className="text-sm text-wt-text-secondary">kWh</span>
              </span>
            </div>
            <div>
              <span className="text-sm text-wt-text-secondary block">Usable</span>
              <span className="text-2xl font-mono-numeric font-bold">
                {(selectedProperty.usable_fraction * 100).toFixed(0)}%
              </span>
            </div>
            <div>
              <span className="text-sm text-wt-text-secondary block">Reserve</span>
              <span className="text-2xl font-mono-numeric font-bold text-status-stale">
                {selectedProperty.battery_reserve_pct}%
              </span>
            </div>
            <div>
              <span className="text-sm text-wt-text-secondary block">Usable Capacity</span>
              <span className="text-2xl font-mono-numeric font-bold text-neon-green">
                {(selectedProperty.battery_kwh_total * selectedProperty.usable_fraction * (1 - selectedProperty.battery_reserve_pct / 100)).toFixed(1)} <span className="text-sm text-wt-text-secondary">kWh</span>
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Battery Health & Capacity (Computed) */}
      <BatteryHealthSection propertyId={selectedPropertyId} />
      {propertyPlants.length > 0 && (
        <Link
          to={`/app/battery-health/${propertyPlants[0].id}`}
          className="text-sm text-primary hover:underline inline-flex items-center gap-1"
        >
          View Detailed Health Report →
        </Link>
      )}

      {/* Generator Status */}
      {generatorStatus?.existsGen && (
        <Card className="bg-wt-bg-secondary border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Power className="w-5 h-5 text-neon-amber" />
              Generator
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge variant={generatorStatus.genOn ? 'default' : 'secondary'} className={generatorStatus.genOn ? 'bg-neon-amber/20 text-neon-amber border-neon-amber/30' : ''}>
                  {generatorStatus.genOn ? 'Running' : 'Off'}
                </Badge>
                {generatorStatus.genOn && generatorStatus.genPower > 0 && (
                  <span className="font-mono-numeric text-lg font-bold text-neon-amber">
                    {generatorStatus.genPower.toLocaleString()} <span className="text-sm text-wt-text-secondary">W</span>
                  </span>
                )}
              </div>
              {!generatorStatus.genOn && (
                <span className="text-sm text-wt-text-secondary">Standby</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Orphan systems */}
      {orphanSystems.length > 0 && (
        <Card className="bg-wt-bg-secondary border-status-stale/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-status-stale">
              <CircuitBoard className="w-5 h-5" />
              Unlinked Devices
            </CardTitle>
            <CardDescription>
              These devices are not linked to any plant
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {orphanSystems.map(system => {
                const pollStatus = getPollingStatus(system);
                return (
                  <div 
                    key={system.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-secondary/30"
                  >
                    <div className="flex items-center gap-3">
                      <CircuitBoard className="w-5 h-5 text-wt-text-secondary" />
                      <div>
                        <span className="font-medium">
                          {system.display_name || system.device_sn || 'Unknown Device'}
                        </span>
                        <div className="text-sm text-wt-text-secondary">
                          <span className="font-mono text-xs">{system.device_sn}</span>
                        </div>
                      </div>
                    </div>
                    <Badge variant="outline" className={pollStatus.className}>
                      {pollStatus.label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
        </>
      )}
    </div>
  );
}
